import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { EDITOR_COMMANDS, formatKeybinding, type EditorCommandContext } from '@/lib/editor-commands';
import { Keyboard, Search } from 'lucide-react';
import { useState } from 'react';

export function ShortcutReference({
  open,
  onOpenChange,
  context,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: EditorCommandContext;
}): React.JSX.Element {
  const [query, setQuery] = useState('');
  const normalized = query.trim().toLowerCase();
  const commands = EDITOR_COMMANDS.filter(
    (command) =>
      command.keybindings.length > 0 &&
      (normalized === '' ||
        [command.label, command.description, command.category, ...command.keywords]
          .join(' ')
          .toLowerCase()
          .includes(normalized))
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>Bindings come from the same command registry as the editor controls.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            aria-label="Search keyboard shortcuts"
            placeholder="Search shortcuts"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="pl-9"
          />
        </div>
        <div className="min-h-0 overflow-y-auto rounded-md border">
          {commands.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">No shortcuts match “{query}”.</p>
          ) : (
            commands.map((command) => (
              <div key={command.id} className="flex items-center gap-4 border-b px-3 py-2.5 last:border-0">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{command.label}</div>
                  <div className="truncate text-xs text-muted-foreground">{command.description}</div>
                </div>
                <div className="flex shrink-0 gap-1" aria-label={`${command.label} shortcuts`}>
                  {command.keybindings.map((binding, index) => (
                    <kbd key={index} className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[11px] shadow-sm">
                      {formatKeybinding(binding)}
                    </kbd>
                  ))}
                </div>
                {!command.enabled(context) ? <span className="sr-only">Currently unavailable</span> : null}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ShortcutReferenceButton({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label="Keyboard shortcuts"
      title={`Keyboard shortcuts (${formatKeybinding({ key: '/', primary: true })})`}
      className="size-8 text-muted-foreground"
      onClick={onClick}
    >
      <Keyboard />
    </Button>
  );
}
